/**
 * @description
 * Client portal layout component for client users.
 * Simplified layout without admin navigation and features.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import React from 'react';
import { Outlet } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { LogOut } from 'lucide-react';

interface ClientPortalLayoutProps {
  children?: React.ReactNode;
}

/**
 * Client portal layout component
 */
export function ClientPortalLayout({ children: _children }: ClientPortalLayoutProps) {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="https://byteable.ai/images/logo.png"
                alt="Byte Logo"
                className="h-8 w-8"
              />
              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  Byte Client Portal
                </h1>
                <p className="text-sm text-muted-foreground">
                  Welcome, {user?.name || 'Client User'}
                </p>
              </div>
            </div>
            
            {/* User info */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">
                  {user?.email}
                </p>
                <p className="text-xs text-muted-foreground">
                  Client Access
                </p>
              </div>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user?.name?.charAt(0) || 'C'}
                </span>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t bg-card mt-auto">
        <div className="container mx-auto px-4 py-4">
          <div className="text-center text-sm text-muted-foreground">
            <p>
              Byte Client Portal - Data Analytics Dashboard
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
